import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import Button from "ds/components/Button";
import useErrorHandle from "hooks/useErrorHandle";
import useCanManageExternalIntegration from "shared/ExternalIntegration/access/useCanManageExternalIntegration";

import IntegrationActionDropdown from "./ActionDropdown";
import { showIntegrationDetailsDrawer } from "./Drawer";
import { GET_EXTERNAL_INTEGRATION } from "./gql";
import PageLayout from "./PageLayout";
import { ExternalIntegrationQueryResponse } from "./types";
import IntegrationUsedBy from "./UsedBy";

const ServiceNowIntegrationDetailsPage = () => {
  const params = useParams<{ integrationId?: string }>();
  const canManageExternalIntegration = useCanManageExternalIntegration();
  const navigate = useNavigate();

  const { integrationId } = params;

  const { loading, data, error } = useQuery<ExternalIntegrationQueryResponse>(
    GET_EXTERNAL_INTEGRATION,
    {
      variables: {
        integrationId,
      },
    }
  );

  const openDetailsDrawer = useCallback(() => {
    if (data?.externalIntegration) {
      showIntegrationDetailsDrawer({
        integration: data.externalIntegration,
        id: data.externalIntegration.id,
      });
    }
  }, [data]);

  const onDelete = useCallback(() => {
    navigate(`/integrations/servicenow`);
  }, [navigate]);

  const ErrorContent = useErrorHandle(error);

  const canManageIntegration = useMemo(() => {
    if (!data?.externalIntegration) {
      return false;
    }
    return canManageExternalIntegration(data.externalIntegration);
  }, [data, canManageExternalIntegration]);

  const actions = useMemo(() => {
    return data?.externalIntegration ? (
      <>
        <Button variant="secondary" onPress={openDetailsDrawer}>
          Details
        </Button>
        <IntegrationActionDropdown
          size="medium"
          integration={data.externalIntegration}
          refetchQueriesOnDelete={["GetExternalIntegrations"]}
          canManageIntegration={canManageIntegration}
          showDetails={false}
          onDelete={onDelete}
        />
      </>
    ) : null;
  }, [data, canManageIntegration, openDetailsDrawer, onDelete]);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLayoutSkeleton />;
  }

  if (!integrationId || !data?.externalIntegration) {
    return <NotFoundPage />;
  }

  return (
    <PageLayout actions={actions} data={data}>
      <IntegrationUsedBy
        name={data.externalIntegration.name}
        attachedBluePrints={data.externalIntegration.attachedBlueprints}
        loading={loading}
      />
    </PageLayout>
  );
};

export default ServiceNowIntegrationDetailsPage;
